import {
  MemberRole,
  ApiPeerGroup,
  BaseUser,
  OrgMembership,
  BasePeerGroup,
  convertPeerGroup,
  ApiOrgMember,
  RelationshipManager
} from '.';
import { BasePacket, Objective, Outcome } from './packet';
import { updateFirstWhere } from '../utils';

interface Relationships<T> {
  [key: number]: ({
    // We have to actually add the key here, since Object.keys can't
    // (for good reason) properly type the key
    key: number;
  } & T)[];
}

type RoleRelationships = Relationships<{
  role: MemberRole;
  manager: RelationshipManager;
  managerReference?: string;
}>;
type BasicRelationships = Relationships<{}>;

const addRelationship = <T>(
  relationships: Relationships<T>,
  id: number,
  relationship: { key: number } & T
): Relationships<T> => {
  return {
    ...relationships,
    [id]: [...relationships[id], relationship]
  };
};

const removeRelationship = <T>(
  relationships: Relationships<T>,
  id: number,
  key: number
): Relationships<T> => {
  return {
    ...relationships,
    [id]: relationships[id].filter(r => r.key !== key)
  };
};

const updateRelationship = <T>(
  relationships: Relationships<T>,
  id: number,
  relationship: { key: number } & T
): Relationships<T> => {
  return {
    ...relationships,
    [id]: [
      ...relationships[id].filter(({ key }) => key !== relationship.key),
      relationship
    ]
  };
};

interface BooleanMap {
  [key: number]: boolean;
}

export interface OrgPeerGroupBase extends BasePeerGroup {
  memberCount: number;
  packets: BasePacket[];
}

// A group has members
export interface PeerGroupMember extends BaseUser {
  role: MemberRole;
  manager: RelationshipManager;
  managerReference?: string;
}

// A user has memberships
export interface PeerGroupMembership extends OrgPeerGroupBase {
  role: MemberRole;
  manager: RelationshipManager;
  managerReference?: string;
}

export interface OrgUser extends BaseUser {
  memberships: PeerGroupMembership[];
  isOrphan: boolean;
  isAdmin: boolean;
  isOrganizer: boolean;
  isHomeOrg: boolean;
}

// A group has members
export interface OrgPeerGroup extends OrgPeerGroupBase {
  members: PeerGroupMember[];
}

export interface OrgPacket {
  packet: BasePacket;
  peerGroups: BasePeerGroup[];
}

export const UNRULR_MANAGER = { type: 'unrulr' };

// Treated as immutable
export class OrgStructure {
  // Calculated fields
  readonly users: OrgUser[];
  readonly peerGroups: OrgPeerGroup[];
  readonly packets: OrgPacket[];

  constructor(
    private groupMembers: RoleRelationships,
    private userMemberships: RoleRelationships,
    private groupPackets: BasicRelationships,
    private packetGroups: BasicRelationships,
    private userOrgConnection: BooleanMap,
    private orgMemberships: OrgMembership[],
    private basePeerGroups: BasePeerGroup[],
    private basePackets: BasePacket[]
  ) {
    const userMap = orgMemberships.reduce(
      (agg: { [key: number]: OrgMembership }, orgMember) => {
        agg[orgMember.member.userId] = orgMember;

        return agg;
      },
      {}
    );

    const peerGroupMap = basePeerGroups.reduce(
      (agg: { [key: number]: BasePeerGroup }, basePeerGroup) => {
        agg[basePeerGroup.peerGroupId] = basePeerGroup;

        return agg;
      },
      {}
    );

    const packetMap = basePackets.reduce(
      (agg: { [key: number]: BasePacket }, basePacket) => {
        agg[basePacket.packetId] = basePacket;
        return agg;
      },
      {}
    );

    this.users = orgMemberships.map(om =>
      this.inflateOrgUser(om, peerGroupMap, packetMap)
    );

    this.peerGroups = basePeerGroups.map(bpg =>
      this.inflateOrgPeerGroup(bpg, userMap, packetMap)
    );

    this.packets = basePackets.map(bp =>
      this.inflateOrgPacket(bp, peerGroupMap)
    );
  }

  static empty(): OrgStructure {
    return new OrgStructure({}, {}, {}, {}, {}, [], [], []);
  }

  private inflateOrgUser(
    orgMember: OrgMembership,
    peerGroupMap: { [key: number]: BasePeerGroup },
    packetMap: { [key: number]: BasePacket }
  ): OrgUser {
    const userId = orgMember.member.userId;
    const relationships = this.userMemberships[userId];

    const memberships = relationships.map(({ key, ...fields }) => {
      const basePG = peerGroupMap[key];

      return {
        ...basePG,
        ...fields,
        packets: this.groupPackets[basePG.peerGroupId].map(
          ({ key }) => packetMap[key]
        ),
        memberCount: this.groupMembers[basePG.peerGroupId].length
      };
    });

    return {
      ...orgMember.member,
      memberships,
      isOrphan: orgMember.isOrphan,
      isAdmin: orgMember.isAdmin,
      isOrganizer: orgMember.isOrganizer,
      isHomeOrg: orgMember.isHomeOrg
    };
  }

  private inflateOrgPeerGroup(
    basePeerGroup: BasePeerGroup,
    userMap: { [key: number]: OrgMembership },
    packetMap: { [key: number]: BasePacket }
  ) {
    const members = this.groupMembers[basePeerGroup.peerGroupId].map(
      ({ key, ...fields }) => ({
        ...userMap[key].member,
        ...fields
      })
    );

    const packets = this.groupPackets[basePeerGroup.peerGroupId].map(
      ({ key }) => packetMap[key]
    );

    return {
      ...basePeerGroup,
      packets,
      members,
      memberCount: members.length
    };
  }

  private inflateOrgPacket(
    basePacket: BasePacket,
    peerGroupMap: { [key: number]: BasePeerGroup }
  ): OrgPacket {
    const peerGroups = this.packetGroups[basePacket.packetId].map(
      ({ key }) => peerGroupMap[key]
    );

    return {
      packet: basePacket,
      peerGroups
    };
  }

  addPeerGroup(peerGroup: BasePeerGroup) {
    return this.copyWith({
      basePeerGroups: [...this.basePeerGroups, peerGroup],
      groupMembers: {
        ...this.groupMembers,
        [peerGroup.peerGroupId]: []
      },
      groupPackets: {
        ...this.groupPackets,
        [peerGroup.peerGroupId]: []
      }
    });
  }

  updatePeerGroup(peerGroup: BasePeerGroup) {
    return this.copyWith({
      basePeerGroups: updateFirstWhere(
        this.basePeerGroups,
        pg => pg.peerGroupId === peerGroup.peerGroupId,
        () => convertPeerGroup(peerGroup)
      )
    });
  }

  connectGroupToUser(peerGroupId: number, userId: number, role: MemberRole) {
    return this.copyWith({
      groupMembers: addRelationship(this.groupMembers, peerGroupId, {
        key: userId,
        role,
        manager: UNRULR_MANAGER
      }),
      userMemberships: addRelationship(this.userMemberships, userId, {
        key: peerGroupId,
        role,
        manager: UNRULR_MANAGER
      })
    });
  }

  disconnectGroupFromUser(peerGroupId: number, userId: number) {
    return this.copyWith({
      groupMembers: removeRelationship(this.groupMembers, peerGroupId, userId),
      userMemberships: removeRelationship(
        this.userMemberships,
        userId,
        peerGroupId
      )
    });
  }

  updateGroupUserConnection(
    peerGroupId: number,
    userId: number,
    role: MemberRole
  ) {
    return this.copyWith({
      groupMembers: updateRelationship(this.groupMembers, peerGroupId, {
        key: userId,
        role,
        manager: UNRULR_MANAGER
      }),
      userMemberships: updateRelationship(this.userMemberships, userId, {
        key: peerGroupId,
        role,
        manager: UNRULR_MANAGER
      })
    });
  }

  connectGroupToPacket(peerGroupId: number, packetId: number) {
    return this.copyWith({
      groupPackets: addRelationship(this.groupPackets, peerGroupId, {
        key: packetId
      }),
      packetGroups: addRelationship(this.packetGroups, packetId, {
        key: peerGroupId
      })
    });
  }

  disconnectGroupFromPacket(peerGroupId: number, packetId: number) {
    return this.copyWith({
      groupPackets: removeRelationship(
        this.groupPackets,
        peerGroupId,
        packetId
      ),
      packetGroups: removeRelationship(this.packetGroups, packetId, peerGroupId)
    });
  }

  // This can initialize relationships with a negative userId, if you're
  // using a temporary ID for a new user.  If so, call `updateUserId`
  // after the api user-create is complete.
  addUser(user: BaseUser, { isAdmin = false, isOrganizer = false } = {}) {
    return this.copyWith({
      orgMemberships: [
        ...this.orgMemberships,
        {
          member: user,
          isOrganizer,
          isAdmin,
          isOrphan: false,
          isHomeOrg: true
        }
      ],
      userMemberships: {
        ...this.userMemberships,
        [user.userId]: []
      },
      userOrgConnections: {
        ...this.userOrgConnection,
        [user.userId]: true
      }
    });
  }

  updateMemberships(memberships: OrgMembership[]) {
    // These wouldn't be included in the response to a membership update
    // api call.
    const orphans = this.orgMemberships.filter(um => um.isOrphan);

    return this.copyWith({ orgMemberships: [...orphans, ...memberships] });
  }

  // Needed when we get the real id back after a create user api call.
  updateUserId(tempUserId: number, userId: number) {
    // swap out the index
    const {
      [tempUserId]: memberships,
      ...otherUserMemberships
    } = this.userMemberships;
    const {
      // eslint-disable-next-line  @typescript-eslint/no-unused-vars
      [tempUserId]: isConnected,
      ...otherUserOrgConnections
    } = this.userOrgConnection;

    const membershipIndex = this.orgMemberships.findIndex(
      om => om.member.userId === tempUserId
    );

    const userMembership = this.orgMemberships[membershipIndex];

    const updatedMembership = {
      ...userMembership,
      member: { ...userMembership.member, userId: userId }
    };

    return this.copyWith({
      orgMemberships: [
        ...this.orgMemberships.filter(om => om.member.userId !== tempUserId),
        updatedMembership
      ],
      userMemberships: {
        ...otherUserMemberships,
        [userId]: memberships
      },
      userOrgConnections: {
        ...otherUserOrgConnections,
        [userId]: true
      }
    });
  }

  updateUser(user: BaseUser) {
    const membershipIndex = this.orgMemberships.findIndex(
      om => om.member.userId === user.userId
    );

    const existingMembership = this.orgMemberships[membershipIndex];

    const updatedMembership = {
      ...existingMembership,
      member: { ...existingMembership.member, ...user }
    };

    return this.copyWith({
      orgMemberships: [
        ...this.orgMemberships.filter(om => om.member.userId !== user.userId),
        updatedMembership
      ]
    });
  }

  addPacket(packet: BasePacket) {
    return this.copyWith({
      basePackets: this.basePackets.concat([packet]),
      packetGroups: {
        ...this.packetGroups,
        [packet.packetId]: []
      }
    });
  }

  updatePacket(packetId: number, updateFn: (p: BasePacket) => BasePacket) {
    return this.copyWith({
      basePackets: updateFirstWhere(
        this.basePackets,
        p => p.packetId == packetId,
        updateFn
      )
    });
  }

  updatePacketObjective(packetId: number, objective: Objective) {
    return this.updatePacket(packetId, p => ({
      ...p,
      objectives: updateFirstWhere(
        p.objectives,
        o => o.objectiveId === objective.objectiveId,
        () => objective
      )
    }));
  }

  addPacketObjective(packetId: number, objective: Objective) {
    return this.updatePacket(packetId, p => ({
      ...p,
      objectives: p.objectives.concat([objective])
    }));
  }

  updatePacketOutcome(packetId: number, outcome: Outcome) {
    return this.updatePacket(packetId, p => ({
      ...p,
      outcomes: updateFirstWhere(
        p.outcomes,
        out => out.outcomeId === outcome.outcomeId,
        () => outcome
      )
    }));
  }

  setPacketOutcomes(packetId: number, outcomes: Outcome[]) {
    return this.updatePacket(packetId, p => ({
      ...p,
      outcomes
    }));
  }

  setPacketObjectives(packetId: number, objectives: Objective[]) {
    return this.updatePacket(packetId, p => ({
      ...p,
      objectives
    }));
  }

  deletePacketOutcome(packetId: number, outcomeId: number) {
    return this.updatePacket(packetId, p => ({
      ...p,
      outcomes: p.outcomes.filter(o => o.outcomeId !== outcomeId)
    }));
  }

  deletePacketObjective(packetId: number, objectiveId: number) {
    return this.updatePacket(packetId, p => ({
      ...p,
      objectives: p.objectives.filter(o => o.objectiveId !== objectiveId)
    }));
  }

  // Sets one member's org role flags without an api round trip.
  updateUserRoleFlags(
    userId: number,
    { isAdmin, isOrganizer }: { isAdmin: boolean; isOrganizer: boolean }
  ) {
    return this.copyWith({
      orgMemberships: this.orgMemberships.map(om =>
        om.member.userId === userId ? { ...om, isAdmin, isOrganizer } : om
      )
    });
  }

  // Only really useful (for now) in the case where add a user fails and we
  // want to remove the temporary user used to track the request progress.
  //
  // We haven't yet decided how to handle removing a user from the org:
  // - should we delete the user entirely?
  // - should we just remove them from the org?
  // - what do we do with the work they've created?
  //
  // So for now, this won't operate on relationships at all.
  removeTemporaryUser(userId: number) {
    return this.copyWith({
      orgMemberships: this.orgMemberships.filter(
        om => om.member.userId !== userId
      )
    });
  }

  private copyWith(partial: {
    groupMembers?: RoleRelationships;
    userMemberships?: RoleRelationships;
    groupPackets?: BasicRelationships;
    packetGroups?: BasicRelationships;
    userOrgConnections?: BooleanMap;
    orgMemberships?: OrgMembership[];
    basePeerGroups?: BasePeerGroup[];
    basePackets?: BasePacket[];
  }) {
    return new OrgStructure(
      partial.groupMembers || this.groupMembers,
      partial.userMemberships || this.userMemberships,
      partial.groupPackets || this.groupPackets,
      partial.packetGroups || this.packetGroups,
      partial.userOrgConnections || this.userOrgConnection,
      partial.orgMemberships || this.orgMemberships,
      partial.basePeerGroups || this.basePeerGroups,
      partial.basePackets || this.basePackets
    );
  }
}

export const buildFromApi = (
  apiGroups: ApiPeerGroup[],
  orgMemberships: ApiOrgMember[],
  basePackets: BasePacket[]
): OrgStructure => {
  // We have to initialize the userMembership map because we build it by
  // reducing over the list of groups.
  //
  // If we don't initialize here, then every time we set
  // `userMemberships[userId][peerGroupId] = ...` we will need to check
  // if `userMemberships[userId]` has been initialized.
  //
  // This way seems simpler

  // Merge members of groups with org users.
  //
  // In our legacy (well, current) world, users don't have to be
  // members of an org to connect with a group in that org, and we sometimes
  // add ourselves to groups manually (because google classroom) and that
  // doesn't create a group membership.
  const allGroupUsers: OrgMembership[] = apiGroups
    .flatMap(({ memberships }) => memberships)
    .map(({ user }) => ({
      member: {
        ...user,
        // Hack job to handle different payloads coming back?
        emails: user.emails || []
      },
      isAdmin: false,
      isOrganizer: false,
      isHomeOrg: true,
      isOrphan: true
    }));

  const usersById: { [key: number]: OrgMembership } = {};

  // By listing the group users first, we set the default values for
  // `isAdmin`, `isOrganizer` and `isOrphan`.
  //
  // The ApiOrgMember[] will overwrite all memberships with explicit
  // values from the api.
  [
    ...allGroupUsers,
    ...orgMemberships.map(m => ({ ...m, isOrphan: false }))
  ].forEach(u => {
    usersById[u.member.userId] = u;
  });

  // Initialized here, and then passed into the reduce
  const userMemberships: RoleRelationships = {};

  const usersUnion = Object.values(usersById);

  // Use both users in groups and org members to initialize relationships
  usersUnion.forEach(
    ({ member: { userId } }) => (userMemberships[userId] = [])
  );

  const groupMembers: RoleRelationships = {};

  // Build groupMembers map.
  apiGroups.forEach(({ memberships, peerGroupId }) => {
    const idRolePairs = memberships.map(m => ({
      key: m.user.userId,
      role: m.role,
      manager: m.manager,
      managerReference: m.managerReference
    }));

    // Add all users as members of a group
    groupMembers[peerGroupId] = idRolePairs;

    // Add this peerGroup as a membership of all users
    idRolePairs.forEach(({ key, role, manager, managerReference }) => {
      const test = userMemberships[key];

      if (!test) {
        console.log('missing info', { key, role });
      }

      userMemberships[key] = [
        ...(userMemberships[key] ?? []),
        {
          key: peerGroupId,
          role: role,
          manager: manager,
          managerReference: managerReference
        }
      ];
    });
  });

  const groupPackets = apiGroups.reduce(
    (agg: BasicRelationships, { packets, peerGroupId }) => {
      agg[peerGroupId] = packets.map(({ packetId }) => ({
        key: packetId
      }));

      return agg;
    },
    {}
  );

  const packetGroups = apiGroups.reduce(
    (agg: BasicRelationships, { packets, peerGroupId }) => {
      packets.forEach(({ packetId }) => {
        const current = agg[packetId] || [];

        agg[packetId] = [...current, { key: peerGroupId }];
      });

      return agg;
    },
    {}
  );

  // Initialize relationships for any packets which are not in groups.
  basePackets.forEach(({ packetId }) => {
    if (!packetGroups[packetId]) {
      packetGroups[packetId] = [];
    }
  });

  const userOrgConnection = orgMemberships.reduce((agg: BooleanMap, ou) => {
    agg[ou.member.userId] = true;
    return agg;
  }, {});

  const basePeerGroups = apiGroups.map(convertPeerGroup);

  return new OrgStructure(
    groupMembers,
    userMemberships,
    groupPackets,
    packetGroups,
    userOrgConnection,
    usersUnion,
    basePeerGroups,
    basePackets
  );
};
